import React, { createContext, useContext, useReducer, useEffect, ReactNode, useState } from 'react';
import { Student, LedgerEntry, SupabaseState, Lesson, Notification, UserRole, Teacher, Parent, baseStudent, NotificationType, baseTeacher } from '../components/interfaces/All';
import {useAuth} from './AuthContext';
import { useNavigate } from 'react-router-dom';

const root_domain = window.location.hostname;
let API_BASE = '/api';
if(root_domain === 'localhost' || root_domain === '127.0.0.1') {
  API_BASE = 'https://preprod.crm.rivieraapps.com/api';
} 

type SupabaseAction =
  | { type: 'SET_ONLINE', payload: boolean }
  | { type: 'SET_STUDENTS', payload: Student[] }
  | { type: 'SET_TEACHERS', payload: Teacher[] }
  | { type: 'SET_PARENTS', payload: Parent[] }
  | { type: 'SET_NOTIFICATIONS', payload: Notification[] }
  | { type: 'SET_LESSONS', payload: Lesson[] }
  | { type: 'SET_TIMESLOT_SUGGESTIONS', payload: { start: Date; end: Date }[] }
  | { type: 'SET_TIMESLOTS_OF_THE_DAY', payload: { start: Date; end: Date }[] }
  | { type: 'SET_LEDGER', payload: LedgerEntry[] }
  | { type: 'SET_USER_AUTH_ID', payload: string }
  | { type: 'SET_USER_PROFILE', payload: Student | Teacher | Parent }
  | { type: 'SET_USER_ROLE', payload: UserRole }
  | { type: 'UPDATE_STUDENT', payload: Partial<Student> }
  | { type: 'UPDATE_PARENT', payload: Partial<Teacher>  }
  | { type: 'UPDATE_TEACHER', payload: Partial<Teacher>  }
  | { type: 'SYNC_START' }
  | { type: 'SYNC_END' };

const initialState: SupabaseState = {
    availableSlots: [],
    allTimeslotswOfTheDay: [],
    userRole: undefined,
    students: [],
    userProfile: baseStudent,
    parents: [],
    teachers: [],
    ledger: [],
    lessons: [],
    notifications: [],
    isOnline: navigator.onLine,
    isSyncing: false,
    lastSync: localStorage.getItem('lastSync')
};

const supabaseReducer = (state: SupabaseState, action: SupabaseAction): SupabaseState => {

  switch (action.type) {
    case 'SET_ONLINE':
      return { ...state, isOnline: action.payload };
    case 'SET_USER_PROFILE':
        localStorage.setItem('userProfile', JSON.stringify(action.payload));
        return { ...state, userProfile: action.payload };
    case 'SET_STUDENTS':
        localStorage.setItem('students', JSON.stringify(action.payload));
        return { ...state, students: action.payload };
    case 'SET_TEACHERS':
        localStorage.setItem('teachers', JSON.stringify(action.payload));
        return { ...state, teachers: action.payload };
    case 'SET_PARENTS':
        localStorage.setItem('parents', JSON.stringify(action.payload));
        return { ...state, parents: action.payload };
    case 'SET_NOTIFICATIONS':
        localStorage.setItem('notifications', JSON.stringify(action.payload));
        return { ...state, notifications: action.payload };
    case 'SET_LEDGER':
        localStorage.setItem('ledger', JSON.stringify(action.payload));
        return { ...state, ledger: action.payload };
    case 'SET_LESSONS':
        localStorage.setItem('lessons', JSON.stringify(action.payload));
        return { ...state, lessons: action.payload };
    case 'SET_TIMESLOTS_OF_THE_DAY':
        localStorage.setItem('allTimeslotswOfTheDay', JSON.stringify(action.payload));
        return { ...state, allTimeslotswOfTheDay: action.payload };
    case 'SET_TIMESLOT_SUGGESTIONS':
        localStorage.setItem('availableSlots', JSON.stringify(action.payload));
        return { ...state, availableSlots: action.payload };
    case 'SET_USER_ROLE':
        localStorage.setItem('userRole', JSON.stringify(action.payload));
        return { ...state, userRole: action.payload };
    case 'UPDATE_STUDENT':
        const updatedStudents = state.students.map(student =>
            student.id === action.payload.id ? { ...student, ...action.payload } : student
        );
        localStorage.setItem('students', JSON.stringify(updatedStudents));
        return { ...state, students: updatedStudents };
    case 'UPDATE_TEACHER':
        const updatedTeachers = state.teachers.map(teacher =>
            teacher.id === action.payload.id
            ? { ...teacher, ...action.payload }
            : teacher
        );
        localStorage.setItem('teachers', JSON.stringify(updatedTeachers));
        return { ...state, teachers: updatedTeachers };
    case 'SYNC_START':
      return { ...state, isSyncing: true };
    case 'SYNC_END':
      return { ...state, isSyncing: false, lastSync: new Date().toISOString() };
    default:
      return state;
  }
};

interface SupabaseContextType {
    state: SupabaseState;

    savePairingCode: (code: string) => { error: any };

    fetchMyLedger: (teacher_id?:string) => Promise<void>;
    addCashPayment: (studentId: string, amount: number, teacherId?:string) => Promise<void>;

    fetchLessons: () => Promise<void>;
    updateLesson: (data: any) => Promise<void>;

    findAvailableSlots: (date: Date, afterThisHour?: Date) => Promise<void>;
    allTimeslotswOfTheDay: (date: Date) => Promise<{ start: Date; end: Date }[]>;

    cancelLesson: (lessonId: string) => Promise<void>;
    removeLesson: (lessonId: string) => Promise<void>;
    completeLesson: (lessonId: string) => Promise<void>;
    markNoShowLesson: (lessonId: string) => Promise<void>;
    rescheduleLesson: (lessonId: string) => Promise<void>;

    fetchNotifications: () => Promise<void>;

    changeUserRole: ( newRole: UserRole, userId?: string) => Promise<void>;
    createUserRole: (role: UserRole, authId: string, userId?: string) => Promise<void>;

    fetchUserRole: () => Promise<void>;
    addLessonFeedbackTeacher: (lessonId: string, feedback: string, rating: number) => Promise<void>;
    addLessonFeedbackStudent: (lessonId: string, feedback: string, rating: number) => Promise<void>;
    bookALesson: (studentId: string, title: string, start: Date, end: Date, teacherId?: string) => Promise<void>;

    fetchStudents: () => Promise<void>;
    updateStudentBalance: (id: string, balance: number) => Promise<void>;
    updateStudentData: (data: Partial<Student>) => Promise<void>;
    createStudent: (formData: Omit<Student, 'id' | 'teacher_id'>) => Promise<void>;
    toggleStudentStatus: (id: string, currentStatus: 'active' | 'paused' | 'inactive') => Promise<void>;
    deleteStudent: (id: string) => Promise<void>;
    connectStudentAccountwithAuthId: (data: {email: string, auth_id: string})  => Promise<void>;

    updateParentData: ( data: Partial<Parent>) => Promise<void>;
    createParent:(formData: Omit<Parent, 'id' >) => Promise<void>;
    deleteParent: (id: string) => Promise<void>;
    fetchParents: () => Promise<void>;
    connectparentAccountwithAuthId: (data: {email: string, auth_id: string})  => Promise<void>;

    deleteTeacher: (id: string) => Promise<void>;
    fetchTeachers: () => Promise<void>;
    updateTeacherData: ( data: Partial<Teacher>) => Promise<void>;
    createTeacher:(formData: Omit<Teacher, 'id' >) => Promise<void>;

    syncAll: (dispatch:React.Dispatch<SupabaseAction>) => Promise<void>;
}

const SupabaseContext = createContext<SupabaseContextType | undefined>(undefined);

export const SupabaseProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [state, dispatch] = useReducer(supabaseReducer, initialState);
    const { session, loading } = useAuth();
    const [bearer, setbearer] = useState<string>();

    useEffect(() => {
        if(loading || !session) {return};
        setbearer(session.access_token);
        const handleOnline = () => { dispatch({ type: 'SET_ONLINE', payload: true });}
        const handleOffline = () => dispatch({ type: 'SET_ONLINE', payload: false });

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, [session, loading]);

    // Auto-sync when back online
    useEffect(() => {
        if(loading || !session) {return};
        if(bearer === undefined) return;
        if (state.isOnline && !state.isSyncing) {
            syncAll(dispatch, state);
        }
    }, [state.isOnline, bearer, loading]);

    const getAuthHeaders = async (session_bearer:string) => {
        const headers = {
            'Authorization': `Bearer ${session_bearer}`,
            'Content-Type': 'application/json'
        };
        if(session_bearer === undefined || !headers.Authorization || headers.Authorization.length < 20) {
            console.error('Invalid authorization token:', headers.Authorization);
            return null;
        }

        return headers;
    };

    // HELPERS
    const convertTeacher = (teacher:any)=>{
        if(!teacher) {
            console.error('Teacher object is null!')
            return { baseTeacher }; // fallback
        }
        let settings = teacher.teacher_settings;
        if (!settings) return { ...teacher, teacher_settings: null };

        // Handle double-stringified: "\"{...}\"" → "{...}" → object
        if (typeof settings === 'string') {
            try {
                // Remove outer quotes if double-stringified
                if (settings.startsWith('"') && settings.endsWith('"')) {
                    settings = settings.slice(1, -1);
                }
                settings = JSON.parse(settings);
            } catch (e) {
                console.warn('Failed to parse teacher_settings:', e);
                return { ...teacher, teacher_settings: null };
            }
        }
        else{
            // the object might already be converted
            if('breaks' in settings){
                return { ...teacher, teacher_settings: settings };
            }else{
                return { ...teacher, teacher_settings: baseTeacher.teacher_settings };
            }
        }

        return { ...teacher, teacher_settings: settings };
    }

    const helper_createSomethingPost = async (data: any, type: 'student' | 'teacher' | 'parent') => {
        try {
            const headers = await getAuthHeaders(bearer);
            if (!headers) {console.log('Retry later'); return;}
            const response = await fetch(`${API_BASE}/${type}s`, {
                method: 'POST',
                headers,
                body: JSON.stringify(data)
            });
            if (!response.ok) throw new Error('Create failed');
            if (type=='student') await fetchStudents();
            if (type=='teacher') await fetchTeachers();
            if (type=='parent') await fetchParents();
        } catch (error) {
            console.error(`Create ${type} failed:`, error);
            throw error
        }
    }

    const helper_updateSomethingPatch = async (data: any, type: 'student' | 'teacher' | 'parent' | 'lesson') => {
        try {
            const headers = await getAuthHeaders(bearer);
            if (!headers) {console.log('Retry later'); return;}
            const response = await fetch(`${API_BASE}/${type}s`, {
                method: 'PATCH',
                headers,
                body: JSON.stringify(data)
            });

            if (!response.ok) throw new Error('Update failed');
            if(type == 'lesson') await fetchLessons();
        } catch (error) {
            console.error(`Update failed ${type}:`, error);
            console.log('Printing data', data, 'type', type)
        }
    }

    // const helper_updateAccountUsingEmailPatch = async (data: any, type: 'student' | 'parent' ) => {
    //     try {
    //         const headers = await getAuthHeaders(bearer);
    //         if (!headers) {console.log('Retry later'); return;}
    //         if (!('email' in data) || !('auth_id' in data)) {console.error('No email or auth_id found where should be provided')}
    //         const response = await fetch(`${API_BASE}/students`, {
    //             method: 'PATCH',
    //             headers,
    //             body: JSON.stringify(data)
    //         });

    //         if (!response.ok) throw new Error('Update failed');
    //     } catch (error) {
    //         console.error(`Update failed ${type}:`, error);
    //         console.log('Printing data', data, 'type', type)
    //     }
    // }

    const helper_fetchSomethingGet = async( url: string ): Promise<{ data:any, error: any }> => {
        try {
            console.log("Fetching:", `${API_BASE}/${url}` );
            const headers = await getAuthHeaders(bearer);
            if (!headers) {console.log('Retry later'); return{ data: [] , error: 'Unauthorized' };}
            if (!headers.Authorization) {
                console.error('No authorization header found');
                return { data: [], error: 'Profile not found' };
            };
            const response = await fetch(`${API_BASE}/${url}`, {
                method: 'GET', headers
            } );
            if (!response.ok){
                if(response.status === 404) {
                    return { data: [], error: null };
                }else{
                    throw new Error(`Fetch failed: ${response.status}`);
                }
            }
            const jsonData = await response.json();
            return { data: jsonData, error: null };
        } catch (error) {
            // console.log('Error fetching: ', url);
            // console.error('Failed to fetch: helper_fetchSomethingGet', error);
            return { data: [] , error}
        }
    }

    const helper_deleteSomethingDelete = async( url: string ) => {
        try {
            const headers = await getAuthHeaders(bearer);
            if (!headers) {console.log('Retry later'); return;}
            const response = await fetch(`${API_BASE}/${url}`, {
                method: 'DELETE',
                headers
            });

            if (!response.ok) throw new Error('Create failed');
        } catch (error) {
            console.error(`Delete ${url} failed:`, error);
            throw error
        }
    }

    const savePairingCode = async (code: string) => {
        try {
            const headers = await getAuthHeaders(bearer);
            const response = await fetch(`${API_BASE}/pairing-codes`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ code })
            });
            dispatch({ type: 'SET_PAIRING_CODE', payload: code });
            return { error: null };
        } catch (error) {
            console.error('Failed to save pairing code:', error);
            return { error };
        }
    }

    const createNotification = async (
        title: string,
        shortText: string,
        type: NotificationType,
        destination: string,
        targetUsersIds?: string[]  // Optional: notify specific user (student)
    ) => {
        return;
        // const user = state.userProfile;
        // if (!user.data.user?.id) return;

        // const currentUserId = user.data.user.id;

        // 1. Always notify CURRENT user (teacher)
        // await supabase.from('notifications').insert({
        //     user_id: currentUserId,
        //     type,
        //     title,
        //     short_text: shortText,
        //     destination,
        //     expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
        // });

        // for each connected user, add the notification
        // targetUsersIds?.forEach(async (user_id)=>{
        //     await supabase.from('notifications').insert({
        //         user_id: user_id,
        //         type,
        //         title,
        //         short_text: shortText,
        //         destination,
        //         expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
        //     });
        // })
    };

    const bookALesson = async (studentId: string, title: string, start: Date, end: Date, teacherId?: string) => {
        try {
            const teacher_id =  teacherId?  teacherId: state.userProfile.id; // if teacherId provided use it, otherwise use the logged in teacher's id
            const headers = await getAuthHeaders(bearer);
            const lessonObject = {
                student_id: studentId,
                title,
                start_time: start.toISOString(),
                end_time: end.toISOString(),
                teacher_id,
                status: 'confirmed',
            }
            const response = await fetch(`${API_BASE}/lessons/book`, {
                method: 'POST',
                headers,
                body: JSON.stringify(lessonObject)
            });

            if (!response.ok) throw new Error('Booking failed');

            await fetchLessons();
        } catch (error) {
            console.error('Booking failed:', error);
            throw error;
        }
    };

    const allTimeslotswOfTheDay = async (date: Date) => {
        console.log("calling allTimeslotswOfTheDay for date", date);
        const slots = [];
        if (!state.userProfile || state.userRole !== 'teacher') return [];
        const teacherSettings = state.userProfile.teacher_settings? state.userProfile.teacher_settings : baseTeacher.teacher_settings;
        const timeslotDurationMin = teacherSettings.sessionLengthMinutes;
        const workingHours = teacherSettings.workingHours? teacherSettings.workingHours : baseTeacher.teacher_settings.workingHours;
        const workingDays = teacherSettings.workingDays? teacherSettings.workingDays : baseTeacher.teacher_settings.workingDays;
        const breaks = teacherSettings.breaks? teacherSettings.breaks : baseTeacher.teacher_settings.breaks;
        const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();

        if (!workingDays.includes(dayOfWeek)) {
            return [];
        }

        for (let time = new Date(date.setHours(0, 0, 0, 0)).getTime(); time < new Date(date.setHours(23, 59, 59, 999)).getTime(); time += timeslotDurationMin * 60 * 1000) {
            const slotStart = new Date(time);
            const slotEnd = new Date(time + timeslotDurationMin * 60 * 1000);

            // Check if within working hours
            const workingHour = workingHours[dayOfWeek];
            if (workingHour) {
                const [workStartHour, workStartMin] = workingHour.start.split(':').map(Number);
                const [workEndHour, workEndMin] = workingHour.end.split(':').map(Number);
                const workStart = new Date(date.setHours(workStartHour, workStartMin, 0, 0));
                const workEnd = new Date(date.setHours(workEndHour, workEndMin, 0, 0));

                if (slotStart < workStart || slotEnd > workEnd) {
                    continue;
                }
            }

            // Check if overlaps with breaks
            const overlapsBreak = breaks.some((breakTime) => {
                const breakStart = new Date(breakTime.start);
                const breakEnd = new Date(breakTime.end);
                return slotStart < breakEnd && slotEnd > breakStart;
            });

            if (!overlapsBreak) {
                slots.push({ start: slotStart, end: slotEnd });
            }
        }

        dispatch({ type: 'SET_TIMESLOTS_OF_THE_DAY', payload: slots });
    }


    const findAvailableSlots = async (date: Date, afterThisHour?: Date) => {
        console.log("calling findAvailableSlots", date);

        const user = state.userProfile;
        if (!user) throw new Error('User not authenticated');
        // const teacher_id = user.data.user?.id;

        // 1. Fetch all lessons for the day
        const { data } = await helper_fetchSomethingGet( 'lessons/' );
        const lessons: Lesson[] = data;
        const lessonsOfTheDate = lessons.filter(lesson =>
            new Date(lesson.start_time).toDateString() === date.toDateString() &&
            new Date(lesson.end_time).toDateString() === date.toDateString()
        )
        // 2. Calculate free slots (e.g., 1-hour slots between 8am-8pm)
        const slots = [];
        // TODO set working hours in the teacher profile and exclude hardcoded ones here
        const startOfDay = afterThisHour?
            afterThisHour > new Date(date.setHours(8, 0, 0, 0))? afterThisHour : new Date(date.setHours(8, 0, 0, 0))
            : new Date(date.setHours(8, 0, 0, 0));
        const endOfDay = new Date(date.setHours(20, 0, 0, 0));
        // TODO set timeslot duration in the teacher profile and exclude hardcoded one here
        const timeslotDurationMin = 60; // state.userProfile.teacher_settings.sessionLengthMinutes
        // TODO set breaks in the teacher profile and exclude hardcoded ones here
        const lunchbreaks = [
            { start_time: new Date(date.setHours(12, 0, 0, 0)), end_time: new Date(date.setHours(13, 0, 0, 0)) }
        ];
        const takenTimeslots = [...(lessonsOfTheDate || []), ...lunchbreaks];

        for (let time = startOfDay.getTime(); time < endOfDay.getTime(); time += timeslotDurationMin * 60 * 1000) {
            const slotStart = new Date(time);
            const slotEnd = new Date(time + timeslotDurationMin * 60 * 1000);

            const overlaps = takenTimeslots?.some(lesson => {
                const lessonStart = new Date(lesson.start_time).getTime();
                const lessonEnd = new Date(lesson.end_time).getTime();
                return (slotStart.getTime() < lessonEnd && slotEnd.getTime() > lessonStart);
            });

            if (!overlaps) {
                slots.push({ start: slotStart, end: slotEnd });
            }

        }
        console.log('found ', slots.length, ' timeslots')
        dispatch({ type: 'SET_TIMESLOT_SUGGESTIONS', payload: slots });

    };


    const fetchUserRole = async() => {

        if (!state.isOnline) {
            console.log('Offline');
            return;
        }
        const { data } = await helper_fetchSomethingGet(`myprofile`);
        if (data && 'role' in data) {
            // console.log('User role from /myprofile:', data);
            dispatch({ type: 'SET_USER_ROLE', payload: data.role });
            dispatch({ type: 'SET_USER_PROFILE', payload: data });
        }
    };

    const cancelLesson = async (lessonId: string) => {
        // set lesson as canceled in the database
        await helper_updateSomethingPatch({ id: lessonId, status: 'canceled' }, 'lesson');
        await fetchLessons();
        // TODO: create notification
    };

    const removeLesson = async (lessonId: string) => {
        // delete the lesson from the database
        await helper_deleteSomethingDelete(`lessons/${lessonId}`);
        await fetchLessons();
        // TODO: create notification
    };

    const completeLesson = async (lessonId: string) => {
        // update lesson status as completed in the database
        await helper_updateSomethingPatch({ id: lessonId, status: 'completed' }, 'lesson');
        await fetchLessons();
        // TODO Notify STUDENT about completion
    };

    const rescheduleLesson = async (lessonId: string) => {
        // update lesson status as rescheduled in the database
        await helper_updateSomethingPatch({ id: lessonId, status: 'reschedule' }, 'lesson');
        await fetchLessons();
        // TODO Notify STUDENT about rescheduling
    }

    const markNoShowLesson = async (lessonId: string) => {
        // update lesson status as no-show in the database
        await helper_updateSomethingPatch({ id: lessonId, status: 'no-show' }, 'lesson');
        await fetchLessons();
        // TODO Notify STUDENT about no-show status
    };

    const deleteStudent = async (id: string) => {
        // TODO - ths will delete user but not the lessons, ledger entries... related to the student. We should either cascade delete or handle those relations in the frontend
        try{
            await helper_deleteSomethingDelete(`students/${id}`)
            await fetchStudents();
        }catch(error) {
            console.error('Failed to delete student:', error);
        }
    };

    const deleteParent = async (id: string) => {
        try{
            await helper_deleteSomethingDelete(`parents/${id}`)
            await fetchParents();
        }catch(error) {
            console.error('Failed to delete parent:', error);
        }
    };

    const deleteTeacher = async (id: string) => {
        try{
            await helper_deleteSomethingDelete(`teachers/${id}`)
            await fetchTeachers();
        }catch(error) {
            console.error('Failed to delete teacher:', error);
        }
    };

    const toggleStudentStatus = async (id: string, currentStatus: 'active' | 'paused' | 'inactive') => {
        const newStatus = currentStatus === 'active' ? 'paused' : 'active';
        try {
            helper_updateSomethingPatch({ id, status: newStatus }, 'student');
        } catch (error) {
            console.error('Failed to toggle student status:', error);
        }
    };

    const addLessonFeedbackTeacher = async (lessonId: string, feedback: string, rating: number) => {

        await helper_updateSomethingPatch(
            { id: lessonId, teacher_feedback: feedback, teacher_rating: rating, feedback_at: new Date().toISOString() },
            'lesson'
        );
        // TODO: create notification for the student
    };

    const addLessonFeedbackStudent = async (lessonId: string, feedback: string, rating: number) => {
        helper_updateSomethingPatch({ id: lessonId, student_feedback: feedback, student_rating: rating, feedback_at: new Date().toISOString() }, 'lesson');
        // TODO: create notification for the teacher
    }
    // Fetch today's notifications (auto-expires old ones)
    const fetchNotifications = async () => {

        if (!state.isOnline) {
            console.log('Offline - using local cache');
            return;
        }
        const user = state.userProfile;
        if (!user) throw new Error('User not authenticated');

        const {data}= await helper_fetchSomethingGet(`notifications`);
        dispatch({ type: 'SET_NOTIFICATIONS', payload: data || [] });

    };

    const fetchStudents = async () => {
        if (!state.isOnline) {
            console.log('Offline - using local cache');
            return;
        }
        const user = state.userProfile;
        if (!user) throw new Error('User not authenticated');
        try {
            const {data}= await helper_fetchSomethingGet(`students`);
            dispatch({ type: 'SET_STUDENTS', payload: Array.isArray(data) ? data : [] });
        } catch (err) {
            console.error('Fetch students failed:', err);
            dispatch({ type: 'SET_STUDENTS', payload: [] });
        }

    };

    const fetchTeachers = async () => {
        if (!state.isOnline) return;

        const {data} = await helper_fetchSomethingGet(`teachers`);
        const parsedTeachers = data.map(convertTeacher);
        dispatch({ type: 'SET_TEACHERS', payload: parsedTeachers });
    };

    const fetchParents = async () => {
        if (!state.isOnline) {
            console.log('Offline - using local cache');
            return;
        }

        const user = state.userProfile;
        if (!user) throw new Error('User not authenticated');
        const {data} = await helper_fetchSomethingGet(`parents`);

        const cleanData = data?.map((parent: any) => ({
            ...parent,
            student_id: parent.student_id ? JSON.parse(parent.student_id) : []
        }));
        dispatch({ type: 'SET_PARENTS', payload: cleanData || [] });

    };

    const createTeacher = async (formData: Teacher) => {

        try {
            await helper_createSomethingPost(formData, 'teacher')
        } catch (error) {
            console.error('Failed to update teacher:', error);
        }
    };

    const updateLesson = async (data: Lesson) => {
        helper_updateSomethingPatch(data, 'lesson');
    };

    const fetchLessons = async () => {
        if (!state.isOnline) {
            console.log('Offline - cannot fetch lessons');
            return;
        }

        const user = state.userProfile;
        if (!user) throw new Error('User not authenticated');
        const {data}= await helper_fetchSomethingGet(`lessons`);

        // Transform for UI
        const lessons: Lesson[] = (data || []).map(item => ({
            id: item.id,
            time: new Date(item.start_time).toLocaleString(), // ✅ Uses start_time
            title: item.title,
            duration: `${item.duration_minutes || 60}min`, // ✅ duration_minutes
            student: state.students.find(s => s.id === item.student_id)?.first_name || 'Unknown',
            student_id: item.student_id,
            teacher: state.teachers.find(s => s.id === item.teacher_id)?.first_name || 'Unknown',
            teacher_id: item.teacher_id,
            status: item.status,
            location: item.location,
            notes: item.notes,
            student_rating: item.student_rating,
            student_feedback: item.student_feedback,
            teacher_feedback: item.teacher_feedback,
            teacher_rating: item.teacher_rating,
            feedback_at: item.feedback_at,
        }));

        dispatch({ type: 'SET_LESSONS', payload: lessons });

    };

    const fetchMyLedger = async (teacher_id?: string) => {
        if (!state.isOnline || !teacher_id) return;
        const teacherId = teacher_id?`/${teacher_id}`:''
        const {data} = await helper_fetchSomethingGet('ledger'+ teacherId)
        dispatch({ type: 'SET_LEDGER', payload: data }); // ledgerItems: LedgerEntry[]
    };

    const updateStudentBalance = async (id: string, balance: number) => {
        dispatch({ type: 'UPDATE_STUDENT', payload: { id, balance } });
        if (!state.isOnline) { console.log('Offline - balance queued for sync');  return; }
        helper_updateSomethingPatch({ id, balance }, 'student');
    };

    const updateStudentData = async (data: Partial<Student>) => {
        dispatch({ type: 'UPDATE_STUDENT', payload: { ...data } });
        if (!state.isOnline) return;
        helper_updateSomethingPatch(data, 'student');
    };

    const updateParentData = async (data: Partial<Parent>) => {
        dispatch({ type: 'UPDATE_PARENT', payload: { ...data } });
        if (!state.isOnline) return;
        helper_updateSomethingPatch(data, 'parent');
    }

    const updateTeacherData = async (data: Partial<Teacher>) => {
        dispatch({ type: 'UPDATE_TEACHER', payload: { ...data } });
        if (!state.isOnline) return;
        helper_updateSomethingPatch(data, 'teacher');
    };

    const createStudent = async (formData: any) => {
        delete formData.id;
        delete formData.auth_id
        delete formData.auth_provider
        helper_createSomethingPost(formData, 'student')
    };

    const connectStudentAccountwithAuthId = async (data: any) => {
        if (!data.email || !data.auth_id) {
            console.error('Invalid student data');
            return;
        }
        await helper_updateSomethingPatch(data, 'student');
        await syncAll(dispatch, state);
        await fetchTeachers();
    }

    const connectParentAccountwithAuthId = async (data: any) => {
        if (!data.email || !data.auth_id) {
            console.error('Invalid parent data');
            return;
        }
        await helper_updateSomethingPatch(data, 'parent');
        await syncAll(dispatch, state);
        await fetchTeachers();
    }

    const createParent = async (formData: any) => {

        try {
            const user = state.userProfile;
            if (!user) throw new Error('User not authenticated');
            // const teacherId = user.data.user?.id;

            delete formData.id;
            delete formData.auth_id
            delete formData.auth_provider

            const cleanData = {
                ...formData,
                teacher_id: formData.teacher_id[0],
                student_id: formData.student_id.join(','),
            };

            helper_fetchSomethingGet(`parents`).then(({data, error})=>{
                const existing_parent = data?.find((parent: any) => parent.email === formData.email);
                if(error){
                    console.error('Error checking existing parent:', error);
                    return;
                }
                if(existing_parent){
                    // Update existing
                    helper_updateSomethingPatch(cleanData, 'parent');
                } else {
                    // Create new
                    helper_createSomethingPost(cleanData , 'parent')
                }
            })
            fetchParents();
        } catch (error) {
            console.error('Create student failed:', error);
            throw error;
        }
    };

    const addCashPayment = async (studentId: string, amount: number, teacherId?: string) => {
        try {
            const headers = await getAuthHeaders(bearer);
            const response = await fetch(`${API_BASE}/payments/cash`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ studentId, amount, teacherId })
            });

            if (!response.ok) throw new Error('Payment failed');

            await fetchMyLedger(teacherId);
        } catch (error) {
            console.error('Payment failed:', error);
            throw error;
        }
    };

    const syncAll = async (dispatch: React.Dispatch<SupabaseAction>, state: SupabaseState) => {
        if (!state.isOnline || state.isSyncing) return;
        dispatch({ type: 'SYNC_START' });

        try {
            await fetchUserRole();
        } catch (error) {
            console.error('SyncAll failed - fetchUserRole:', error);
            dispatch({ type: 'SYNC_END' });
        }
        try {
            fetchTeachers();
            fetchStudents();
            fetchLessons();
            fetchMyLedger();
            localStorage.setItem('lastSync', new Date().toISOString());
            dispatch({ type: 'SYNC_END' });
        } catch (error) {
            console.error('SyncAll failed - fetchData:', error);
            dispatch({ type: 'SYNC_END' });
        }
    };

    return (
        <SupabaseContext.Provider value={{
            state,
            savePairingCode,
            // teachers
            fetchTeachers,
            createTeacher, deleteTeacher, updateTeacherData,
            // students
            fetchStudents,
            toggleStudentStatus,
            // parents
            fetchParents,
            createStudent,
            connectStudentAccountwithAuthId,
            deleteStudent,
            updateStudentData,

            createParent,
            deleteParent,
            updateParentData,
            connectParentAccountwithAuthId,
            // notifications
            fetchNotifications,
            // ledger
            fetchMyLedger,
            addCashPayment,
            updateStudentBalance,
            // lessons
            updateLesson,
            fetchLessons,
            cancelLesson,
            removeLesson,
            completeLesson,
            markNoShowLesson,
            rescheduleLesson,
            bookALesson,
            addLessonFeedbackTeacher,
            addLessonFeedbackStudent,
            findAvailableSlots,
            allTimeslotswOfTheDay,
            // miscelanious,
            fetchUserRole,

            syncAll
        }}>
        {children}
        </SupabaseContext.Provider>
    );
};


// Custom hook
export const useSupabase = () => {
  const context = useContext(SupabaseContext);
  if (!context) {
    throw new Error('useSupabase must be used within SupabaseProvider');
  }
  return context;
};
